/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The page structure (`GET /api/page-structure`: which sections a public page
 * shows, in which order, plus the admin's own blocks) as every page that has
 * one reads it.
 *
 * Seeded synchronously, so the first render is the baked page: the prerender
 * seed first (the SSR HTML and the hydrating tree agree on the entries), else
 * the in-memory cache for an instant SPA back-navigation, else null (the page
 * walks its own fallback order). After mount it is fetched again in case the
 * bake is stale. That answer is usually exactly the seed, but a fresh parse is
 * a new object, and handing it to setState re-rendered the whole page right
 * after hydration (CLAUDE.md, "Nothing re-renders the page right after
 * hydration"): equal JSON keeps the seeded object and React skips the render;
 * a real change lands as a transition, never blocking a tap; a failed refetch
 * leaves the seed on screen.
 *
 * One hook for the homepage and the ten pages that used to carry their own
 * copy of this effect — six of them without the equality check.
 */

import { startTransition, useEffect, useState } from 'react';
import { usePrerender } from '../contexts/PrerenderContext';
import { cachedFetch, readCache } from '../lib/apiCache';
import { sameJson } from '../lib/sameJson';
import type { PageStructureFile } from '../types/pageStructure';

const PAGE_STRUCTURE_URL = '/api/page-structure';

export function usePageStructure(): PageStructureFile | null {
  const prerender = usePrerender();
  const [pageStructure, setPageStructure] = useState<PageStructureFile | null>(
    () => prerender?.pageStructure ?? readCache<PageStructureFile>(PAGE_STRUCTURE_URL) ?? null
  );

  useEffect(() => {
    let cancelled = false;
    cachedFetch<PageStructureFile>(PAGE_STRUCTURE_URL)
      .then((data) => {
        if (cancelled) return;
        startTransition(() => setPageStructure((prev) => (sameJson(prev, data) ? prev : data)));
      })
      .catch(() => {
        // Keep the seed (or nothing): the page falls back to its default order.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return pageStructure;
}
